<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { isPhoneE164OrEmpty, isNumber } from 'shared/helpers/Validators';

import Button from 'next/button/Button.vue';
import ViewWrapper from './ViewWrapper.vue';
import ChannelWhatsAppEmbeddedSignupView from './ChannelWhatsAppEmbeddedSignupView.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const inboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const uiFlags = useMapGetter('inboxes/getUIFlags');

const showManualSetup = ref(false);

const hasWhatsappAppId = computed(
  () =>
    window.chatwootConfig?.whatsappAppId &&
    window.chatwootConfig.whatsappAppId !== 'none'
);

const showEmbeddedSignup = computed(
  () => hasWhatsappAppId.value && !showManualSetup.value
);

const inboxName = ref('');
const phoneNumber = ref('');
const apiKey = ref('');
const phoneNumberId = ref('');
const businessAccountId = ref('');

const rules = computed(() => ({
  inboxName: { required },
  phoneNumber: { required, isPhoneE164OrEmpty },
  apiKey: { required },
  phoneNumberId: { required, isNumber },
  businessAccountId: { required, isNumber },
}));

const v$ = useVuelidate(rules, {
  inboxName,
  phoneNumber,
  apiKey,
  phoneNumberId,
  businessAccountId,
});

const hasAnyWhatsAppInbox = computed(() => inboxes.value?.length > 0);

onMounted(() => {
  store.dispatch('inboxes/get');
});

const createChannel = async () => {
  if (hasAnyWhatsAppInbox.value && !v$.value.$dirty) {
    router.push({ name: 'wgpt_onboarding_channels' });
    return;
  }

  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    await store.dispatch('inboxes/createChannel', {
      name: inboxName.value?.trim(),
      channel: {
        type: 'whatsapp',
        phone_number: phoneNumber.value,
        provider: 'whatsapp_cloud',
        provider_config: {
          api_key: apiKey.value,
          phone_number_id: phoneNumberId.value,
          business_account_id: businessAccountId.value,
        },
      },
    });
    router.push({ name: 'wgpt_onboarding_channels' });
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE'));
  }
};

watch(
  inboxes,
  ([inbox]) => {
    if (!inbox) return;
    const { name, phone_number, provider_config } = inbox || {};
    const { api_key, phone_number_id, business_account_id } =
      provider_config || {};
    inboxName.value = name || '';
    phoneNumber.value = phone_number || '';
    apiKey.value = api_key || '';
    phoneNumberId.value = phone_number_id || '';
    businessAccountId.value = business_account_id || '';
  },
  { immediate: true }
);
</script>

<template>
  <ChannelWhatsAppEmbeddedSignupView
    v-if="showEmbeddedSignup"
    @use-manual-setup="showManualSetup = true"
  />

  <ViewWrapper v-else>
    <form
      class="flex flex-col gap-4 max-w-[400px]"
      @submit.prevent="createChannel"
    >
      <label :class="{ error: v$.inboxName.$error }">
        <span class="block text-sm font-medium text-n-slate-12 mb-1">
          {{ t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
        </span>
        <input
          v-model="inboxName"
          type="text"
          class="block w-full"
          :placeholder="t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">
          {{ t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR') }}
        </span>
      </label>

      <label :class="{ error: v$.phoneNumber.$error }">
        <span class="block text-sm font-medium text-n-slate-12 mb-1">
          {{ t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL') }}
        </span>
        <input
          v-model="phoneNumber"
          type="text"
          class="block w-full"
          :placeholder="t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.PLACEHOLDER')"
          @blur="v$.phoneNumber.$touch"
        />
        <span v-if="v$.phoneNumber.$error" class="message">
          {{ t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.ERROR') }}
        </span>
      </label>

      <label :class="{ error: v$.apiKey.$error }">
        <span class="block text-sm font-medium text-n-slate-12 mb-1">
          {{ t('INBOX_MGMT.ADD.WHATSAPP.API_KEY.LABEL') }}
        </span>
        <input
          v-model="apiKey"
          type="text"
          class="block w-full"
          :placeholder="t('INBOX_MGMT.ADD.WHATSAPP.API_KEY.PLACEHOLDER')"
          @blur="v$.apiKey.$touch"
        />
        <span v-if="v$.apiKey.$error" class="message">
          {{ t('INBOX_MGMT.ADD.WHATSAPP.API_KEY.ERROR') }}
        </span>
      </label>

      <label :class="{ error: v$.phoneNumberId.$error }">
        <span class="block text-sm font-medium text-n-slate-12 mb-1">
          {{ t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER_ID.LABEL') }}
        </span>
        <input
          v-model="phoneNumberId"
          type="text"
          class="block w-full"
          :placeholder="
            t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER_ID.PLACEHOLDER')
          "
          @blur="v$.phoneNumberId.$touch"
        />
        <span v-if="v$.phoneNumberId.$error" class="message">
          {{ t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER_ID.ERROR') }}
        </span>
      </label>

      <label :class="{ error: v$.businessAccountId.$error }">
        <span class="block text-sm font-medium text-n-slate-12 mb-1">
          {{ t('INBOX_MGMT.ADD.WHATSAPP.BUSINESS_ACCOUNT_ID.LABEL') }}
        </span>
        <input
          v-model="businessAccountId"
          type="text"
          class="block w-full"
          :placeholder="
            t('INBOX_MGMT.ADD.WHATSAPP.BUSINESS_ACCOUNT_ID.PLACEHOLDER')
          "
          @blur="v$.businessAccountId.$touch"
        />
        <span v-if="v$.businessAccountId.$error" class="message">
          {{ t('INBOX_MGMT.ADD.WHATSAPP.BUSINESS_ACCOUNT_ID.ERROR') }}
        </span>
      </label>
    </form>

    <template #actions>
      <Button
        :label="t('WGPT_ONBOARDING.CHANNELS.BACK')"
        variant="link"
        color="slate"
        size="sm"
        @click="router.push({ name: 'wgpt_onboarding_channels' })"
      />
      <Button
        lg
        :label="t('WGPT_ONBOARDING.CHANNELS.NEXT')"
        :is-loading="uiFlags.isCreating"
        :disabled="uiFlags.isCreating"
        @click="createChannel"
      />
    </template>
  </ViewWrapper>
</template>
